import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { baasSupabase as supabase } from '@/integrations/supabase/baasClient';

interface BaaSUser {
  id: string;
  userId: string;
  email: string;
  displayName: string;
  partnerId: string;
  partnerName: string;
  role: string;
  status: string;
}

interface BaaSAuthContextType {
  user: User | null;
  session: Session | null;
  baasUser: BaaSUser | null;
  baasUserLoaded: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; user?: User | null }>;
  signOut: () => Promise<void>;
  setAuthGuard: (active: boolean) => void;
}

const BaaSAuthContext = createContext<BaaSAuthContextType | undefined>(undefined);

export function BaaSAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [baasUser, setBaaSUser] = useState<BaaSUser | null>(null);
  const [baasUserLoaded, setBaaSUserLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const signingInRef = useRef(false);
  const signOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchBaaSUser = useCallback(async (userId: string) => {
    setBaaSUserLoaded(false);
    try {
      // Look up baas_portal_users membership
      const { data: membership } = await (supabase as any)
        .from('baas_portal_users')
        .select('*, baas_partners(id, legal_name, trading_name, status)')
        .eq('user_id', userId)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();

      if (!membership || !membership.baas_partners) {
        setBaaSUser(null);
        return;
      }

      const { data: { user: authUser } } = await supabase.auth.getUser();
      const meta = authUser?.user_metadata;
      const displayName = meta?.full_name || meta?.name || authUser?.email || '';

      const bu: BaaSUser = {
        id: membership.id,
        userId: membership.user_id,
        email: authUser?.email ?? '',
        displayName,
        partnerId: membership.partner_id,
        partnerName: membership.baas_partners.trading_name ?? membership.baas_partners.legal_name,
        role: membership.role ?? 'viewer',
        status: membership.status,
      };

      setBaaSUser(bu);

      // Update last_login_at via service-role edge action (table writes are locked).
      try {
        await (supabase as any).functions.invoke('baas-portal-settings', {
          body: { action: 'touch_last_login' },
        });
      } catch (e) {
        console.warn('[BaaSAuth] touch_last_login failed:', e);
      }

    } catch (err) {
      console.error('[BaaSAuth] Error fetching BaaS user:', err);
      setBaaSUser(null);
    } finally {
      setBaaSUserLoaded(true);
    }
  }, []);

  const checkMfaComplete = async (): Promise<boolean> => {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (data?.currentLevel === 'aal2') return true;
      if (data?.currentLevel === 'aal1' && data?.nextLevel === 'aal1') return true;
      return false;
    } catch { return false; }
  };

  useEffect(() => {
    let isMounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!isMounted || event === 'INITIAL_SESSION') return;

      if (event === 'SIGNED_OUT') {
        if (signingInRef.current) return;
        if (signOutTimerRef.current) clearTimeout(signOutTimerRef.current);
        signOutTimerRef.current = setTimeout(async () => {
          if (!isMounted || signingInRef.current) return;
          const { data: { session: s } } = await supabase.auth.getSession();
          if (!isMounted || signingInRef.current) return;
          if (!s?.user) {
            setSession(null); setUser(null); setBaaSUser(null); setBaaSUserLoaded(true);
          }
        }, 800);
        return;
      }

      if (signOutTimerRef.current) { clearTimeout(signOutTimerRef.current); signOutTimerRef.current = null; }

      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      if (nextSession?.user) {
        setTimeout(async () => {
          if (!isMounted) return;
          const mfaDone = await checkMfaComplete();
          if (mfaDone) void fetchBaaSUser(nextSession.user.id);
        }, 0);
      } else {
        setBaaSUser(null); setBaaSUserLoaded(true);
      }
    });

    const bootstrap = async () => {
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!isMounted) return;
        setSession(s); setUser(s?.user ?? null);
        if (s?.user) {
          let mfaDone = false;
          for (let i = 0; i < 3; i++) {
            mfaDone = await checkMfaComplete();
            if (mfaDone || !isMounted) break;
            await new Promise(r => setTimeout(r, 500));
          }
          if (mfaDone) await fetchBaaSUser(s.user.id);
          else { setBaaSUser(null); setBaaSUserLoaded(true); }
        } else {
          setBaaSUser(null); setBaaSUserLoaded(true);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    void bootstrap();
    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (signOutTimerRef.current) clearTimeout(signOutTimerRef.current);
    };
  }, [fetchBaaSUser]);

  const setAuthGuard = useCallback((active: boolean) => { signingInRef.current = active; }, []);

  const signIn = async (email: string, password: string) => {
    signingInRef.current = true;
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    signingInRef.current = false;
    if (error) return { error: error as Error };
    if (data?.session) { setSession(data.session); setUser(data.user); void fetchBaaSUser(data.user.id); }
    return { error: null, user: data?.user ?? null };
  };

  const signOut = useCallback(async () => {
    if (signingInRef.current) return;
    await supabase.auth.signOut();
    setBaaSUser(null);
  }, []);

  return (
    <BaaSAuthContext.Provider value={{
      user, session, baasUser, baasUserLoaded, loading,
      signIn, signOut, setAuthGuard,
    }}>
      {children}
    </BaaSAuthContext.Provider>
  );
}

export function useBaaSAuth() {
  const ctx = useContext(BaaSAuthContext);
  if (!ctx) throw new Error('useBaaSAuth must be used within BaaSAuthProvider');
  return ctx;
}
